import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Zap, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api';

/** Polls transcode jobs; exposes helpers for per-target badges + buttons. */
export const useTranscodeJobs = () => {
  const [jobs, setJobs] = useState({});
  const [active, setActive] = useState(false);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const d = await api.request('/api/admin/transcode-jobs');
      const map = {};
      (d.jobs || []).forEach((j) => { map[j.target_id] = j; });
      setJobs(map);
      setActive(!!d.active_job_id || d.queue_size > 0);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    return () => clearTimeout(timer.current);
  }, [refresh]);

  useEffect(() => {
    if (!active) return undefined;
    timer.current = setTimeout(refresh, 3000);
    return () => clearTimeout(timer.current);
  }, [active, jobs, refresh]);

  return { jobs, active, refresh };
};

export const useTranscodeCapacity = () => {
  const [capacity, setCapacity] = useState(null);
  useEffect(() => {
    api.request('/api/admin/transcode-capacity').then(setCapacity).catch(() => {});
  }, []);
  return capacity;
};

export const CapacityNote = ({ capacity }) => {
  if (!capacity?.constrained) return null;
  return (
    <div className="flex items-start gap-2 p-2.5 mb-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded" data-testid="transcode-capacity-note">
      <AlertCircle size={13} className="mt-0.5 shrink-0" />
      <span>
        {capacity.note} Detected: {capacity.cpus} vCPU{capacity.memory_mb ? ` · ${capacity.memory_mb}MB RAM` : ''}.
      </span>
    </div>
  );
};

export const StreamingBadge = ({ target, job, onPrepare, compact = false }) => {
  const [maxHeight, setMaxHeight] = useState('');
  const busy = job && (job.status === 'queued' || job.status === 'running');
  const hasHls = !!target?.hls_url || !!target?.hero_hls_url;
  const noSource = !(target?.video_url || target?.video_url_high || target?.video_url_low || target?.hero_video_url);

  if (busy) {
    return (
      <div className="flex items-center gap-2 text-xs text-blue-700 flex-wrap" data-testid={`stream-progress-${job.target_id}`}>
        <Loader2 size={13} className="animate-spin" />
        <span className="capitalize">{job.stage}</span>
        <div className="w-28 h-1.5 bg-blue-100 rounded overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${job.progress || 0}%` }} /></div>
        <span>{job.progress || 0}%</span>
        {job.speed ? <span className="text-blue-500">{job.speed}</span> : null}
      </div>
    );
  }
  if (noSource) return compact ? null : <span className="text-[11px] text-gray-400">YouTube only — no streaming needed</span>;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {hasHls ? (
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`stream-ready-${target.id || 'hero'}`}>
          <CheckCircle2 size={12} /> Adaptive streaming ready{target.hls_renditions ? ` · ${target.hls_renditions.join(' / ')}` : ''}
        </span>
      ) : job?.status === 'error' ? (
        <span className="inline-flex flex-col text-[11px] text-red-600 max-w-md" data-testid={`stream-error-${job.target_id}`}>
          <span className="inline-flex items-center gap-1"><AlertCircle size={12} /> Encoding failed</span>
          {job.error ? <span className="mt-0.5 font-mono text-[10px] leading-snug text-red-500 break-words whitespace-pre-wrap" data-testid={`stream-error-detail-${job.target_id}`}>{job.error}</span> : null}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid={`stream-missing-${target.id || 'hero'}`}><AlertCircle size={12} /> Not prepared — may stutter on mobile</span>
      )}
      <select
        value={maxHeight}
        onChange={(e) => setMaxHeight(e.target.value)}
        className="text-[11px] border border-gray-300 rounded px-1 py-0.5 bg-white"
        data-testid={`stream-maxheight-${target.id || 'hero'}`}
        title="Highest rendition to encode — lower it if encoding keeps failing on a small server"
      >
        <option value="">Auto quality</option>
        <option value="1080">Up to 1080p</option>
        <option value="720">Up to 720p</option>
        <option value="480">Up to 480p</option>
      </select>
      <button
        type="button"
        onClick={() => onPrepare(maxHeight ? Number(maxHeight) : undefined)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-gray-300 rounded hover:bg-gray-50"
        data-testid={`stream-prepare-${target.id || 'hero'}`}
      >
        {hasHls ? <><RefreshCw size={11} /> Re-encode</> : <><Zap size={11} /> Prepare for streaming</>}
      </button>
    </div>
  );
};
